import { styled, Box as BoxBase, Divider } from '@mui/material';
import { CurrencyAmount } from '@cowbell/ui/core';
import { withShowable } from '../../_global/lib/withShowable';
import { ProductTypes } from '../../../types';
import { P100_RETRO_MAPPING } from '../../workflows/quotes/prime-100/utils/index';

const Box = withShowable(BoxBase);

export default function PolicyHolderQuotePolicySummary({
  children,
  aggLimit,
  limit,
  deductible,
  totalPremium,
  currency,
  uiEffectiveDate,
  uiPolicyExpiresOn,
  uiTerm,
  product,
  waitingPeriod,
  retroactivePeriod,
}: any) {
  const isP100 = ProductTypes.p100 === product;
  const uiWaitingPeriod =
    waitingPeriod == null ? '-' : `${waitingPeriod} Hours`;
  const uiRetroactivePeriod =
    retroactivePeriod == null
      ? '-'
      : P100_RETRO_MAPPING[
          retroactivePeriod as keyof typeof P100_RETRO_MAPPING
        ] ?? '-';

  return (
    <Container>
      <FlexBox justifyContent="space-between">
        <Header>Policy Summary</Header>
        {children}
      </FlexBox>
      <Divider />
      <Box width="100%" pl={1} pr={1} mt={1} pb={1}>
        <FlexBox width="100%">
          <FlexBox flexDirection="column" pr={1} width="42%">
            <FlexBox width="100%" justifyContent="space-between">
              <Text>Aggregate Limit</Text>
              <Text>
                <CurrencyAmount
                  amount={limit ?? aggLimit}
                  currency={currency}
                  notation="compact"
                  minimumFractionDigits={0}
                  maximumFractionDigits={1}
                />
              </Text>
            </FlexBox>
            <FlexBox width="100%" justifyContent="space-between">
              <Text>Deductible</Text>
              <Text>
                <CurrencyAmount
                  amount={deductible}
                  currency={currency}
                  notation="compact"
                  minimumFractionDigits={0}
                  maximumFractionDigits={1}
                />
              </Text>
            </FlexBox>
            <FlexBox width="100%" justifyContent="space-between">
              <BlueText>Total Premium</BlueText>
              <BlueText>
                <CurrencyAmount
                  amount={totalPremium}
                  currency={currency}
                  currencySign="accounting"
                />
              </BlueText>
            </FlexBox>
          </FlexBox>
          <Divider orientation="vertical" flexItem />
          <FlexBox pl={1} flexDirection="column" width="57%">
            <FlexBox width="100%" justifyContent="space-between">
              <Text>Policy Effective</Text>
              <Text>{uiEffectiveDate || '-'}</Text>
            </FlexBox>
            <FlexBox width="100%" justifyContent="space-between">
              <Text>Policy Expiration</Text>
              <Text>{uiPolicyExpiresOn || '-'}</Text>
            </FlexBox>
            <FlexBox width="100%" justifyContent="space-between">
              <Text>Policy Term</Text>
              <Text>{uiTerm ? `${uiTerm} days` : '-'}</Text>
            </FlexBox>
            {isP100 && (
              <>
                <FlexBox width="100%" justifyContent="space-between">
                  <Text>Waiting Period</Text>
                  <Text>{uiWaitingPeriod}</Text>
                </FlexBox>
                <FlexBox width="100%" justifyContent="space-between">
                  <Text>Retroactive Period</Text>
                  <Text>{uiRetroactivePeriod}</Text>
                </FlexBox>
              </>
            )}
          </FlexBox>
        </FlexBox>
      </Box>
    </Container>
  );
}

const Container = styled(Box)(({ theme: { palette } }) => ({
  disapay: 'flex',
  backgroundColor: palette.background.default,
  minHeight: 'rem',
  width: '100%',
  borderRadius: 4,
  padding: '0',
}));

const Header = styled('h3')(({ theme: { config } }) => ({
  margin: 0,
  fontSize: config.textSizes.tertia,
  fontWeight: config.weights.normal,
  padding: '0.5rem',
}));

const Text = styled('p')(({ theme: { config } }) => ({
  margin: 0,
  fontSize: config.textSizes.tertia,
}));

const BlueText = styled(Text)(({ theme: { palette } }) => ({
  color: palette.text.text12,
}));

const FlexBox = withShowable(
  styled(BoxBase)(() => ({
    display: 'flex',
  }))
);
